import React from "react";

function Paginacion({ pagina, totalPaginas, onCambiar }) {
  if (totalPaginas <= 1) return null;

  const pages = [];
  const delta = 2;
  const left  = Math.max(1, pagina - delta);
  const right = Math.min(totalPaginas, pagina + delta);

  if (left > 1) {
    pages.push(1);
    if (left > 2) pages.push("...");
  }
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < totalPaginas) {
    if (right < totalPaginas - 1) pages.push("...");
    pages.push(totalPaginas);
  }

  return (
    <nav className="d-flex justify-content-center mt-3">
      <ul className="pagination pagination-sm mb-0">
        <li className={`page-item ${pagina === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onCambiar(pagina - 1)}>
            <i className="bi bi-chevron-left" />
          </button>
        </li>
        {pages.map((p, i) =>
          p === "..." ? (
            <li key={`e${i}`} className="page-item disabled">
              <span className="page-link">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === pagina ? "active" : ""}`}>
              <button className="page-link" onClick={() => onCambiar(p)}>{p}</button>
            </li>
          )
        )}
        <li className={`page-item ${pagina === totalPaginas ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onCambiar(pagina + 1)}>
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default function MateriasList({
  materias = [],
  loading,
  reload,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  busqueda = "",
  setBusqueda = () => {},
  pagina = 1,
  setPagina = () => {},
  totalFiltradas = 0,
  totalPaginas = 1,
  porPagina = 10,
}) {
  const canManage = canEdit || canDelete;
  const offsetInicio = (pagina - 1) * porPagina;

  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="management-card materias-table-card">
      {/* Cabecera: título + buscador + botón actualizar */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Listado</p>
          <h5 className="mb-0">
            Materias registradas
            {totalFiltradas > 0 && (
              <span className="text-muted fw-normal fs-6 ms-2">
                ({totalFiltradas})
              </span>
            )}
          </h5>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div className="input-group input-group-sm" style={{ width: 220 }}>
            <span className="input-group-text">
              <i className="bi bi-search" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre o sigla…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            {busqueda && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setBusqueda("")}
                title="Limpiar búsqueda"
              >
                <i className="bi bi-x" />
              </button>
            )}
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={reload}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        </div>
      </div>

      {/* Tabla o estado vacío */}
      {materias.length === 0 ? (
        <div className="text-center py-5 text-muted">
          {busqueda ? (
            <>
              <i className="bi bi-search fs-2 d-block mb-2" />
              <p className="mb-0">Sin resultados para <strong>"{busqueda}"</strong>.</p>
              <button
                type="button"
                className="btn btn-link btn-sm mt-1"
                onClick={() => setBusqueda("")}
              >
                Limpiar búsqueda
              </button>
            </>
          ) : (
            <>
              <i className="bi bi-journal-bookmark fs-2 d-block mb-2" />
              <h5 className="fw-semibold mb-1">No hay materias registradas.</h5>
              <p className="mb-0">Crea una materia o actualiza la lista.</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="management-table-wrapper">
            <table className="table align-middle management-table materias-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  <th style={{ width: "35%" }}>Nombre</th>
                  <th style={{ width: "15%" }}>Sigla</th>
                  <th style={{ width: "20%" }}>Color</th>
                  <th style={{ width: "15%" }}>Estado</th>
                  {canManage && <th style={{ width: "10%" }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {materias.map((materia, index) => (
                  <tr key={materia.id}>
                    <td className="text-muted small">{offsetInicio + index + 1}</td>
                    <td className="fw-semibold">{materia.nombre}</td>
                    <td>
                      {materia.sigla ? (
                        <span className="management-pill">
                          <i className="bi bi-tag-fill me-1" />
                          {materia.sigla}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="materias-color-indicator"
                          style={{
                            backgroundColor: materia.color_hex || "#000000",
                            width: "24px",
                            height: "24px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                          }}
                          title={materia.color_hex}
                        />
                        <small className="text-muted font-monospace">
                          {materia.color_hex || "-"}
                        </small>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2 ${
                          materia.activo
                            ? "bg-success-subtle text-success"
                            : "bg-secondary-subtle text-muted"
                        }`}
                      >
                        {materia.activo ? "Activa" : "Inactiva"}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="d-flex gap-2">
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm management-action-btn"
                              onClick={() => onEdit?.(materia)}
                              title="Editar materia"
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm management-action-btn"
                              onClick={() => onDelete?.(materia)}
                              title="Eliminar materia"
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación + contador */}
          <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
            <small className="text-muted">
              Mostrando {offsetInicio + 1}–{Math.min(offsetInicio + porPagina, totalFiltradas)} de {totalFiltradas} materia{totalFiltradas !== 1 ? "s" : ""}
            </small>
            <Paginacion
              pagina={pagina}
              totalPaginas={totalPaginas}
              onCambiar={setPagina}
            />
          </div>
        </>
      )}
    </div>
  );
}
